import { TDocument } from '../documents/documents.interfaces';
import { TActions } from '../__shared/shared.interfaces';

// Base interface for all academic materials
export interface IBaseMaterial {
  id: string;
  name: string;
  description: string;
  category: MaterialCategory;
  status: MaterialStatus;
  condition: MaterialCondition;
  purchaseDate: Date;
  lastMaintenanceDate?: Date;
  location: string;
  quantity: number;
  minimumQuantity: number;
  supplier?: string;
  cost: number;
  tags: string[];
  actions: TActions;
}

// Book specific interface
export interface IBook extends IBaseMaterial {
  type: 'book';
  isbn: string;
  author: string;
  publisher: string;
  edition: string;
  publicationYear: number;
  subject: string;
  gradeLevel: string;
  language: string;
  isDigital: boolean;
  digitalFormat?: 'pdf' | 'epub' | 'mobi';
  digitalUrl?: string;
}

// Lab Equipment specific interface
export interface ILabEquipment extends IBaseMaterial {
  type: 'lab_equipment';
  equipmentType: LabEquipmentType;
  modelNumber: string;
  manufacturer: string;
  serialNumber: string;
  calibrationDate?: Date;
  nextCalibrationDate?: Date;
  powerRequirements?: string;
  safetyInstructions: string;
  maintenanceSchedule: MaintenanceSchedule;
  isPortable: boolean;
  requiresTraining: boolean;
}

// Digital Resource specific interface
export interface IDigitalResource extends IBaseMaterial {
  type: 'digital_resource';
  resourceType: DigitalResourceType;
  format: string;
  fileSize: number;
  url: string;
  accessLevel: AccessLevel;
  licenseType: LicenseType;
  expiryDate?: Date;
  compatibleDevices: string[];
  requiresInternet: boolean;
}

// Stationery specific interface
export interface IStationery extends IBaseMaterial {
  type: 'stationery';
  stationeryType: StationeryType;
  brand: string;
  color?: string;
  size?: string;
  isConsumable: boolean;
  expiryDate?: Date;
}

// Enums for type safety
export enum MaterialCategory {
  BOOK = 'book',
  LAB_EQUIPMENT = 'lab_equipment',
  DIGITAL_RESOURCE = 'digital_resource',
  STATIONERY = 'stationery',
  OTHER = 'other',
}

export enum MaterialStatus {
  AVAILABLE = 'available',
  IN_USE = 'in_use',
  MAINTENANCE = 'maintenance',
  RETIRED = 'retired',
  LOST = 'lost',
  DAMAGED = 'damaged',
}

export enum MaterialCondition {
  NEW = 'new',
  LIKE_NEW = 'like_new',
  GOOD = 'good',
  FAIR = 'fair',
  POOR = 'poor',
}

export enum LabEquipmentType {
  MICROSCOPE = 'microscope',
  BEAKER = 'beaker',
  TEST_TUBE = 'test_tube',
  BUNSEN_BURNER = 'bunsen_burner',
  BALANCE = 'balance',
  CHEMICAL = 'chemical',
  OTHER = 'other',
}

export enum DigitalResourceType {
  EBOOK = 'ebook',
  VIDEO = 'video',
  SOFTWARE = 'software',
  SIMULATION = 'simulation',
  AUDIO = 'audio',
  INTERACTIVE = 'interactive',
  OTHER = 'other',
}

export enum StationeryType {
  PEN = 'pen',
  PENCIL = 'pencil',
  NOTEBOOK = 'notebook',
  PAPER = 'paper',
  FOLDER = 'folder',
  OTHER = 'other',
}

export enum AccessLevel {
  PUBLIC = 'public',
  STUDENT = 'student',
  TEACHER = 'teacher',
  ADMIN = 'admin',
}

export enum LicenseType {
  FREE = 'free',
  PAID = 'paid',
  SUBSCRIPTION = 'subscription',
  ONE_TIME = 'one_time',
}

export interface MaintenanceSchedule {
  frequency: number; // in days
  lastMaintenance: Date;
  nextMaintenance: Date;
  maintenanceType: string;
  maintenanceNotes?: string;
}

// Union type for all material types
export type TAcademicMaterial =
  | IBook
  | ILabEquipment
  | IDigitalResource
  | IStationery;

// Main interface that includes all material types
export interface IAcademicsMaterials {
  id: string;
  name: string;
  description: string;
  documents: {
    receipts: TDocument[];
    invoices: TDocument[];
    payments: TDocument[];
  };
  category: MaterialCategory;
  status: MaterialStatus;
  condition: MaterialCondition;
  purchaseDate: Date;
  lastMaintenanceDate?: Date;
  location: string;
  quantity: number;
  minimumQuantity: number;
  supplier?: string;
  cost: number;
  tags: string[];
  materials: TAcademicMaterial[];
  totalCount: number;
  availableCount: number;
  inUseCount: number;
  maintenanceCount: number;
  retiredCount: number;
  totalValue: number;
  lastInventoryDate: Date;
  department: string;
  notes?: string;
  actions: TActions;
  /** Assigned to students/classes - backend not implemented, use mocks */
  assignedTo?: {
    studentIds?: string[];
    classIds?: string[];
  };
}

// ============================================================
// Material Groups API (Backend Request/Response Types)
// ============================================================

export interface IMaterialGroupListQueryParams {
  page?: number;
  limit?: number;
  instituteId?: string;
  category?: string;
  status?: string;
  department?: string;
  location?: string;
  search?: string;
}

export interface IMaterialGroupRequest {
  material: {
    instituteId: string;
    inventoryId?: string;
    name: string;
    description?: string;
    category: string;
    status: string;
    condition: string;
    purchaseDate?: string;
    lastMaintenanceDate?: string;
    location?: string;
    quantity: number;
    minimumQuantity?: number;
    supplier?: string;
    cost?: number;
    tags?: string[];
    department?: string;
    notes?: string;
    receiptDocumentIds?: string[];
    invoiceDocumentIds?: string[];
    paymentDocumentIds?: string[];
  };
}

export interface IMaterialGroupResponse {
  id: string;
  instituteId: string;
  inventoryId?: string;
  name: string;
  description?: string;
  category: string;
  status: string;
  condition: string;
  purchaseDate?: Date;
  lastMaintenanceDate?: Date;
  location?: string;
  quantity: number;
  minimumQuantity?: number;
  supplier?: string;
  cost?: number;
  tags?: string[];
  department?: string;
  notes?: string;
  documents: {
    receipts: string[];
    invoices: string[];
    payments: string[];
  };
  rollups: {
    totalCount: number;
    availableCount: number;
    inUseCount: number;
    maintenanceCount: number;
    retiredCount: number;
    totalValue: number;
    lastInventoryDate?: Date;
  };
  items?: IMaterialItemResponse[];
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface IMaterialGroupListResponse {
  materials: IMaterialGroupResponse[];
  total: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================================
// Material Items API (Typed Items within Groups)
// ============================================================

export interface IMaterialItemRequest {
  item: {
    itemType: 'book' | 'lab_equipment' | 'digital_resource' | 'stationery';
    name: string;
    description?: string;
    quantity: number;
    status: string;
    // Book fields
    isbn?: string;
    author?: string;
    publisher?: string;
    edition?: string;
    publicationYear?: number;
    subject?: string;
    gradeLevel?: string;
    language?: string;
    isDigital?: boolean;
    digitalFormat?: string;
    digitalUrl?: string;
    // Lab Equipment fields
    equipmentType?: string;
    modelNumber?: string;
    manufacturer?: string;
    serialNumber?: string;
    calibrationDate?: string;
    nextCalibrationDate?: string;
    powerRequirements?: string;
    safetyInstructions?: string;
    maintenanceSchedule?: {
      frequency?: string;
      lastMaintenance?: string;
      nextMaintenance?: string;
      maintenanceType?: string;
      maintenanceNotes?: string;
    };
    isPortable?: boolean;
    requiresTraining?: boolean;
    // Digital Resource fields
    resourceType?: string;
    format?: string;
    fileSize?: string;
    url?: string;
    accessLevel?: string;
    licenseType?: string;
    expiryDate?: string;
    compatibleDevices?: string[];
    requiresInternet?: boolean;
    // Stationery fields
    stationeryType?: string;
    brand?: string;
    color?: string;
    size?: string;
    isConsumable?: boolean;
  };
}

export interface IMaterialItemResponse {
  id: string;
  materialGroupId: string;
  itemType: 'book' | 'lab_equipment' | 'digital_resource' | 'stationery';
  name: string;
  description?: string;
  quantity: number;
  status: string;
  // Book fields
  isbn?: string;
  author?: string;
  publisher?: string;
  edition?: string;
  publicationYear?: number;
  subject?: string;
  gradeLevel?: string;
  language?: string;
  isDigital?: boolean;
  digitalFormat?: string;
  digitalUrl?: string;
  // Lab Equipment fields
  equipmentType?: string;
  modelNumber?: string;
  manufacturer?: string;
  serialNumber?: string;
  calibrationDate?: Date;
  nextCalibrationDate?: Date;
  powerRequirements?: string;
  safetyInstructions?: string;
  maintenanceSchedule?: {
    frequency?: string;
    lastMaintenance?: Date;
    nextMaintenance?: Date;
    maintenanceType?: string;
    maintenanceNotes?: string;
  };
  isPortable?: boolean;
  requiresTraining?: boolean;
  // Digital Resource fields
  resourceType?: string;
  format?: string;
  fileSize?: string;
  url?: string;
  accessLevel?: string;
  licenseType?: string;
  compatibleDevices?: string[];
  requiresInternet?: boolean;
  // Stationery fields (and shared expiryDate for both Digital Resource and Stationery)
  expiryDate?: Date;
  stationeryType?: string;
  brand?: string;
  color?: string;
  size?: string;
  isConsumable?: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
}
